import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  AMBIENTES_MAS_DE,
  BoundingBox,
  FiltrosBusqueda,
  ListaPropiedadesPublica,
  MapaRespuesta,
  PropiedadDetalle,
  RangoPrecios,
} from '../models/propiedad.model';

/**
 * Resultados por página del listado en desktop (#14, reemplaza la decisión
 * D10 de 12): son 17 filas justas en la grilla de 3 columnas de FP-13.
 *
 * **Requiere `backend!63`**, que sube el tope de `porPagina` a 51 en
 * `FiltrarPropiedadesPublicoDto`. Hasta esa MR el DTO validaba `@Max(50)` y
 * el endpoint respondía 400 con 51 —el backend #42 había subido el tope sólo
 * en `FiltrarPropiedadesDto`, el del listado del backoffice—, así que el
 * buscador quedaba sin resultados. Si algún día vuelve a aparecer ese 400,
 * el tope del DTO público es el primer lugar donde mirar.
 */
export const POR_PAGINA_DESKTOP = 51;

/**
 * Resultados por página en mobile (#20). Ahí la grilla es de una sola
 * columna, así que 51 tarjetas son 51 filas de scroll antes de llegar al
 * paginador: con 20 la página se recorre entera y se pagina.
 *
 * Quién elige cuál de los dos va es `BuscadorPage`, que es el que conoce el
 * ancho de pantalla; el servicio sólo pone el default.
 */
export const POR_PAGINA_MOBILE = 20;

/**
 * Cliente del buscador público de propiedades.
 *
 * RN02 (BE-05): la vista de listado y la de mapa comparten los mismos
 * filtros, por eso ambos métodos arman los params con `paramsDeFiltros`.
 */
@Injectable({ providedIn: 'root' })
export class PropiedadesPublicoService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/publico/propiedades`;

  /**
   * GET /publico/propiedades — vista listado (paginada, máx. 51/página).
   *
   * El tamaño de página depende del ancho de pantalla desde **#20**: lo elige
   * el llamador entre `POR_PAGINA_DESKTOP` y `POR_PAGINA_MOBILE` (ver sus
   * JSDoc, arriba). El default es el de desktop.
   */
  listar(
    filtros: FiltrosBusqueda,
    pagina = 1,
    porPagina = POR_PAGINA_DESKTOP,
  ): Observable<ListaPropiedadesPublica> {
    let params = this.paramsDeFiltros(filtros)
      .set('pagina', pagina)
      .set('porPagina', porPagina);
    return this.http.get<ListaPropiedadesPublica>(this.base, { params });
  }

  /**
   * GET /publico/propiedades/mapa — pines para la vista de mapa (BE-04).
   * Acepta bounding box del viewport y límite de pines (BE-06).
   */
  buscarParaMapa(
    filtros: FiltrosBusqueda,
    bbox?: BoundingBox,
    limite = 500,
  ): Observable<MapaRespuesta> {
    let params = this.paramsDeFiltros(filtros).set('limite', limite);
    if (bbox) {
      params = params
        .set('latMin', bbox.latMin)
        .set('latMax', bbox.latMax)
        .set('lngMin', bbox.lngMin)
        .set('lngMax', bbox.lngMax);
    }
    return this.http.get<MapaRespuesta>(`${this.base}/mapa`, { params });
  }

  /** GET /publico/propiedades/:id — ficha pública con galería completa. */
  detalle(id: string): Observable<PropiedadDetalle> {
    return this.http.get<PropiedadDetalle>(`${this.base}/${id}`);
  }

  /**
   * GET /publico/propiedades/rango-precios (BP-01) — extremos de precio por
   * operación y moneda, para los topes del slider (FP-17).
   *
   * Va con `conPrecio: false` **a propósito**: el endpoint responde **400** si
   * recibe cualquiera de las cuatro bandas de precio o sus dos monedas, porque
   * son justamente lo que el slider define a partir de esta respuesta —
   * filtrar por ellas colapsaría los extremos contra la selección vigente.
   * Tampoco acepta paginación, así que este método no la agrega.
   *
   * Es lo natural de equivocar: RN02 dice "mismos filtros" y para todo lo
   * demás lo son, así que reusar `paramsDeFiltros()` tal cual parece correcto
   * y rompe apenas el usuario toca el slider.
   */
  rangoPrecios(filtros: FiltrosBusqueda): Observable<RangoPrecios> {
    return this.http.get<RangoPrecios>(`${this.base}/rango-precios`, {
      params: this.paramsDeFiltros(filtros, { conPrecio: false }),
    });
  }

  /**
   * Params comunes del buscador. `conPrecio: false` omite las cuatro bandas de
   * precio y sus monedas, para el endpoint de rango (ver `rangoPrecios`).
   */
  private paramsDeFiltros(
    filtros: FiltrosBusqueda,
    { conPrecio = true }: { conPrecio?: boolean } = {},
  ): HttpParams {
    let params = new HttpParams();
    if (filtros.tipoPropiedad) {
      params = params.set('tipoPropiedad', filtros.tipoPropiedad);
    }
    if (filtros.operacion) params = params.set('operacion', filtros.operacion);
    // DP02/DP03 (multi-zona): parámetro repetido, el backend combina con OR
    for (const localidad of filtros.localidades ?? []) {
      params = params.append('localidad', localidad);
    }
    if (filtros.buscar) params = params.set('buscar', filtros.buscar);
    if (conPrecio) {
      if (filtros.precioMin != null && filtros.monedaVenta) {
        params = params
          .set('precioMin', filtros.precioMin)
          .set('monedaVenta', filtros.monedaVenta);
      }
      if (filtros.precioMax != null && filtros.monedaVenta) {
        params = params
          .set('precioMax', filtros.precioMax)
          .set('monedaVenta', filtros.monedaVenta);
      }
      if (filtros.precioAlquilerMin != null && filtros.monedaAlquiler) {
        params = params
          .set('precioAlquilerMin', filtros.precioAlquilerMin)
          .set('monedaAlquiler', filtros.monedaAlquiler);
      }
      if (filtros.precioAlquilerMax != null && filtros.monedaAlquiler) {
        params = params
          .set('precioAlquilerMax', filtros.precioAlquilerMax)
          .set('monedaAlquiler', filtros.monedaAlquiler);
      }
    }
    // 1–4: valor exacto (min = max); 5: "+5" (solo min, sin tope)
    if (filtros.ambientes != null) {
      params = params.set('ambientesMin', filtros.ambientes);
      if (filtros.ambientes < AMBIENTES_MAS_DE) {
        params = params.set('ambientesMax', filtros.ambientes);
      }
    }
    // Banda de superficie (BP-04). Cada extremo se manda sólo si está
    // definido: el slider deja `superficieMax` afuera cuando quedó en su tope,
    // que es fijo y no representa el máximo real de las publicaciones.
    if (filtros.superficieMin != null) {
      params = params.set('superficieMin', filtros.superficieMin);
    }
    if (filtros.superficieMax != null) {
      params = params.set('superficieMax', filtros.superficieMax);
    }
    // Flag de emprendimiento (categoría especial de la web pública)
    if (filtros.emprendimiento) {
      params = params.set('emprendimiento', true);
    }
    return params;
  }
}
